import React, { useEffect, useMemo, useState } from 'react';
import { FiCheckSquare, FiChevronLeft, FiChevronRight, FiEdit2 } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import Modal from '../components/ui/Modal';
import { puede } from '../utils/permisos';
import { modulosVisibles } from '../config/modulos';
import { eventosCalendarioPorDia, filtrarCitasCalendario, filtrarTareasCalendario } from '../utils/calendarioUtils';

const ESTADOS = { programada: 'Programada', confirmada: 'Confirmada', completada: 'Completada', cancelada: 'Cancelada' };
const BADGE = { programada: 'badge-info', confirmada: 'badge-ok', completada: 'badge', cancelada: 'badge-danger' };
const ESTADOS_TAREA = { pendiente: 'Pendiente', en_curso: 'En curso', completado: 'Completado' };

function inicioMes(fecha) {
  return new Date(fecha.getFullYear(), fecha.getMonth(), 1);
}

function finMes(fecha) {
  return new Date(fecha.getFullYear(), fecha.getMonth() + 1, 0);
}

function nombreMes(fecha) {
  return fecha.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
}

function formatoFechaCorta(fecha) {
  if (!fecha) return '—';
  return new Date(fecha).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
}

function obtenerDiasMes(fecha) {
  const firstDay = inicioMes(fecha);
  const lastDay = finMes(fecha);
  const startDay = firstDay.getDay();
  const totalDays = lastDay.getDate();
  const semanas = [];
  const cells = [];

  for (let i = 0; i < (startDay + 6) % 7; i += 1) cells.push(null);
  for (let day = 1; day <= totalDays; day += 1) cells.push(new Date(fecha.getFullYear(), fecha.getMonth(), day));
  while (cells.length % 7 !== 0) cells.push(null);

  for (let i = 0; i < cells.length; i += 7) semanas.push(cells.slice(i, i + 7));
  return semanas;
}

export default function Calendario() {
  const { usuario, empresa, empresaActivaId } = useAuth();
  const [citas, setCitas] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [errorTareas, setErrorTareas] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [mesActual, setMesActual] = useState(new Date());
  const [seleccion, setSeleccion] = useState('todos');
  const [modal, setModal] = useState(false);
  const [citaActiva, setCitaActiva] = useState(null);
  const [tareaActiva, setTareaActiva] = useState(null);
  const [diasExpandidos, setDiasExpandidos] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [notificacion, setNotificacion] = useState(null);

  const puedeEditar = puede(usuario, 'citas', 'editar');
  const esSelectorVisible = usuario?.rol === 'superadmin' || usuario?.rol === 'admin';
  const puedeVerTareas = modulosVisibles(usuario, empresa).some((modulo) => modulo.clave === 'tareas');
  const usuarioId = usuario?._id;

  useEffect(() => {
    let vigente = true;
    setCargando(true);
    setCitas([]);
    setClientes([]);
    setUsuarios([]);
    setSeleccion('todos');
    setModal(false);
    setCitaActiva(null);
    setTareaActiva(null);
    setDiasExpandidos({});
    setNotificacion(null);
    async function cargarDatos() {
      try {
        const [citasRes, clientesRes, usuariosRes] = await Promise.all([
          api.get('/citas'),
          api.get('/clientes'),
          api.get('/usuarios'),
        ]);
        if (!vigente) return;
        setCitas(Array.isArray(citasRes.data) ? citasRes.data : []);
        setClientes(Array.isArray(clientesRes.data) ? clientesRes.data : []);
        setUsuarios(Array.isArray(usuariosRes.data) ? usuariosRes.data.filter((u) => u.rol !== 'superadmin') : []);
      } catch (err) {
        if (vigente) setNotificacion({ tipo: 'error', texto: 'No se pudieron cargar las citas del calendario' });
      } finally {
        if (vigente) setCargando(false);
      }
    }

    cargarDatos();
    return () => { vigente = false; };
  }, [usuarioId, empresaActivaId]);

  useEffect(() => {
    let vigente = true;
    setTareas([]);
    setErrorTareas(null);
    setTareaActiva(null);
    setModal(false);
    if (puedeVerTareas && usuarioId) {
      (async () => {
        try {
          const { data } = await api.get('/tareas');
          if (vigente) setTareas(Array.isArray(data) ? data : []);
        } catch (err) {
          if (vigente) setErrorTareas('No se pudieron cargar las tareas del calendario');
        }
      })();
    }
    return () => { vigente = false; };
  }, [puedeVerTareas, usuarioId, empresaActivaId]);

  const citasVisibles = useMemo(() => filtrarCitasCalendario(citas, usuario, seleccion), [citas, usuario, seleccion]);

  const tareasVisibles = useMemo(() => puedeVerTareas ? filtrarTareasCalendario(tareas, usuario) : [], [tareas, usuario, puedeVerTareas]);
  const eventosPorDia = useMemo(() => eventosCalendarioPorDia(citasVisibles, tareasVisibles), [citasVisibles, tareasVisibles]);

  const abrirDetalle = (evento) => {
    setCitaActiva(evento.tipo === 'cita' ? evento.dato : null);
    setTareaActiva(evento.tipo === 'tarea' ? evento.dato : null);
    setModal(true);
  };

  const cambiarEstado = async (nuevoEstado) => {
    if (!citaActiva || tareaActiva) return;
    setGuardando(true);
    try {
      const { data } = await api.put(`/citas/${citaActiva._id}`, { estado: nuevoEstado });
      setCitas((prev) => prev.map((item) => (item._id === data._id ? data : item)));
      setCitaActiva(data);
      setNotificacion({ tipo: 'success', texto: 'Estado actualizado correctamente' });
    } catch (err) {
      setNotificacion({ tipo: 'error', texto: err.response?.data?.error || 'No se pudo actualizar el estado' });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Calendario</h1>
          <p className="page-subtitle">Vista mensual de todas las citas y seguimiento de estados.</p>
        </div>
      </div>

      {notificacion && <div className={`alert-${notificacion.tipo}`} role="status" style={{ marginBottom: 14 }}>{notificacion.texto}</div>}
      {puedeVerTareas && errorTareas && <div className="alert-error" role="alert" style={{ marginBottom: 14 }}>{errorTareas}</div>}

      <div className="page-help-card" role="status">
        <div>
          <strong>Calendario profesional</strong>
          <p>Por defecto se muestran todas las citas de la empresa. Los administradores pueden cambiar el filtro por trabajador y los empleados solo ven su propio calendario.</p>
        </div>
        <div className="page-summary">
          <span className="page-pill">{citasVisibles.length} citas</span>
          {puedeVerTareas && <span className="page-pill">{tareasVisibles.length} tareas propias</span>}
        </div>
      </div>

      <div className="calendar-toolbar">
        <div className="calendar-nav">
          <button className="icon-btn" onClick={() => setMesActual(new Date(mesActual.getFullYear(), mesActual.getMonth() - 1, 1))}><FiChevronLeft /></button>
          <span className="calendar-month">{nombreMes(mesActual)}</span>
          <button className="icon-btn" onClick={() => setMesActual(new Date(mesActual.getFullYear(), mesActual.getMonth() + 1, 1))}><FiChevronRight /></button>
        </div>
        {esSelectorVisible && (
          <select className="topbar-empresa-select" value={seleccion} onChange={(e) => setSeleccion(e.target.value)}>
            <option value="todos">Todos los trabajadores</option>
            {usuarios.map((usuarioItem) => (
              <option key={usuarioItem._id} value={usuarioItem._id}>{usuarioItem.nombre} {usuarioItem.apellidos}</option>
            ))}
          </select>
        )}
      </div>

      {cargando ? <p className="empty">Cargando calendario…</p> : (
        <div className="calendar-grid">
          {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((dia) => <div key={dia} className="calendar-head">{dia}</div>)}
          {obtenerDiasMes(mesActual).flat().map((dia, idx) => {
            const activo = dia && dia.getMonth() === mesActual.getMonth();
            const clave = dia ? `${dia.getFullYear()}-${dia.getMonth()}-${dia.getDate()}` : `empty-${idx}`;
            const eventos = dia ? (eventosPorDia.get(clave) || []) : [];
            return (
              <div key={clave} className={`calendar-cell ${activo ? 'calendar-cell-active' : ''}`}>
                <div className="calendar-day-number">{dia ? dia.getDate() : ''}</div>
                <div className="calendar-events">
                  {(diasExpandidos[clave] ? eventos : eventos.slice(0, 3)).map((evento) => (
                    <button
                      key={`${evento.tipo}-${evento.dato._id}`}
                      className={`calendar-event ${evento.tipo === 'tarea' ? 'calendar-event-task' : BADGE[evento.dato.estado] || ''}`}
                      title={`${evento.tipo === 'tarea' ? 'Tarea' : 'Cita'}: ${evento.dato.titulo}`}
                      aria-label={`${evento.tipo === 'tarea' ? 'Tarea' : 'Cita'}: ${evento.dato.titulo}`}
                      onClick={evento.tipo === 'tarea' ? () => abrirDetalle(evento) : undefined}
                      onDoubleClick={evento.tipo === 'cita' ? () => abrirDetalle(evento) : undefined}
                    >
                      {evento.tipo === 'tarea' && <><FiCheckSquare aria-hidden="true" /> Tarea: </>}{evento.dato.titulo}
                    </button>
                  ))}
                  {eventos.length > 3 && (
                    <button
                      type="button"
                      className="calendar-more"
                      aria-expanded={Boolean(diasExpandidos[clave])}
                      aria-label={`${diasExpandidos[clave] ? 'Ver menos' : 'Ver más'} eventos del ${dia.toLocaleDateString('es-ES')}`}
                      onClick={() => setDiasExpandidos((prev) => ({ ...prev, [clave]: !prev[clave] }))}
                    >{diasExpandidos[clave] ? 'Ver menos' : `Ver más (+${eventos.length - 3})`}</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal abierto={modal && (!tareaActiva || puedeVerTareas)} onCerrar={() => setModal(false)} titulo={tareaActiva ? 'Detalle de la tarea' : 'Detalle de la cita'} ancho={600}>
        {tareaActiva && puedeVerTareas ? (
          <div className="form-grid">
            <div className="field span-2"><strong>{tareaActiva.titulo}</strong></div>
            <div className="field"><span>Estado</span><div>{ESTADOS_TAREA[tareaActiva.estado] || tareaActiva.estado}</div></div>
            <div className="field"><span>Vencimiento</span><div>{formatoFechaCorta(tareaActiva.fechaTope)}</div></div>
            <div className="field span-2"><span>Descripción</span><div>{tareaActiva.descripcion || 'Sin descripción'}</div></div>
            <div className="form-actions span-2">
              <button type="button" className="btn" onClick={() => setModal(false)}>Cerrar</button>
              <Link className="btn btn-primary" to="/app/tareas"><FiCheckSquare /> Ir a tareas</Link>
            </div>
          </div>
        ) : citaActiva ? (
          <div className="form-grid">
            <div className="field span-2">
              <strong>{citaActiva.titulo}</strong>
              <p className="page-subtitle">{formatoFechaCorta(citaActiva.inicio)} – {formatoFechaCorta(citaActiva.fin)}</p>
            </div>
            <div className="field">
              <span>Cliente</span>
              <div>{clientes.find((cliente) => String(cliente._id) === String(citaActiva.clienteId?._id || citaActiva.clienteId))?.nombre || 'Sin cliente'}</div>
            </div>
            <div className="field">
              <span>Responsable</span>
              <div>{citaActiva.asignadoA?.nombre ? `${citaActiva.asignadoA.nombre} ${citaActiva.asignadoA.apellidos || ''}`.trim() : 'Sin asignar'}</div>
            </div>
            <div className="field span-2">
              <span>Estado</span>
              <select value={citaActiva.estado} onChange={(e) => cambiarEstado(e.target.value)} disabled={!puedeEditar || guardando}>
                {Object.entries(ESTADOS).map(([valor, texto]) => <option key={valor} value={valor}>{texto}</option>)}
              </select>
            </div>
            <div className="field span-2">
              <span>Notas</span>
              <div>{citaActiva.notas || 'Sin notas'}</div>
            </div>
            <div className="form-actions span-2">
              <button type="button" className="btn" onClick={() => setModal(false)}>Cerrar</button>
              {puedeEditar && <button type="button" className="btn btn-primary" onClick={() => setModal(false)}><FiEdit2 /> Editar en agenda</button>}
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
